<script setup>
/**
 * AudioRecorder — refatorado para o redesign do Sadraque (#280).
 *
 * Estados internos: 'requesting' | 'denied' | 'recording' | 'paused' | 'preview'
 *
 * Layout (estado recording/paused):
 *   [Trash] [Timer + REC dot] [Waveform scrolling] [Pause/Play btn]
 *
 * Layout (estado preview):
 *   [Play btn] [Waveform com progresso + cursor] [Timer] [Trash]
 *
 * Emits:
 *  - finish-record (File)         — preserva interface antiga, dispara quando user clica
 *                                    no "check" do footer pra ir pra preview. ReplyBox
 *                                    seta hasRecordedAudio=true e isRecordingAudio=false.
 *  - recorder-progress-changed    — preserva interface antiga (string mm:ss)
 *  - play / pause                 — preserva interface antiga (do playback do preview)
 *  - cancel                       — NOVO. User descartou. Parent fecha o recorder e
 *                                    limpa o blob (toggleAudioRecorder + reset).
 *
 * Expose:
 *  - playPause()      — preserva interface antiga (parent pode chamar)
 *  - stopRecording()  — preserva interface antiga
 *  - finalizeRecording() — alias semântico para "ir pra preview"
 *  - cancelAll()      — descarta e fecha
 *
 * Marker: SZ_AUDIO_280_v1_20260520
 */
import getUuid from 'widget/helpers/uuid';
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';

const props = defineProps({
  audioRecordFormat: { type: String, required: true },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
  'cancel',
]);

// ── State machine ─────────────────────────────────────────────────────────
const state = ref('requesting'); // requesting | denied | recording | paused | preview
const errorMessage = ref('');
const elapsedText = ref('0:00');
const previewFile = ref(null);
const wasCancelled = ref(false);
const progressLeft = ref(0); // % horizontal do cursor de progresso no preview

// ── Refs DOM/WS ───────────────────────────────────────────────────────────
const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);

const isPreviewPlaying = ref(false);

// ── Helpers ───────────────────────────────────────────────────────────────
const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes || 0, duration.seconds || 0),
    'm:ss'
  );
};

const browserPermissionHint = computed(() => {
  const ua = navigator.userAgent || '';
  if (/Edg\//.test(ua)) {
    return 'No Edge: ícone de cadeado ao lado da URL → Permissões deste site → Microfone → Permitir.';
  }
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) {
    return 'No Chrome: ícone de cadeado ao lado da URL → Microfone → Permitir.';
  }
  if (/Firefox\//.test(ua)) {
    return 'No Firefox: ícone de escudo/cadeado ao lado da URL → Permissões → Microfone → Permitir.';
  }
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
    return 'No Safari (macOS): Ajustes do sistema → Privacidade → Microfone → Safari, e no Safari: Ajustes → Sites → Microfone.';
  }
  return 'Procure pelo ícone de cadeado ou permissões do site no seu navegador e libere o microfone.';
});

// ── Wavesurfer init ───────────────────────────────────────────────────────
const initWaveSurfer = () => {
  const isDark =
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark');

  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    // Bars conforme tokens Sadraque #280: 2px wide, 2px gap, 4px radius, verde #78BB7B
    waveColor: '#78BB7B',
    progressColor: isDark ? '#FFFFFF' : '#3A8B3A',
    height: 28,
    barWidth: 2,
    barGap: 2,
    barRadius: 4,
    cursorWidth: 0,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: true,
      }),
    ],
  });

  wavesurfer.value.on('pause', () => {
    isPreviewPlaying.value = false;
    emit('pause');
  });
  wavesurfer.value.on('play', () => {
    isPreviewPlaying.value = true;
    emit('play');
  });
  wavesurfer.value.on('finish', () => {
    isPreviewPlaying.value = false;
    progressLeft.value = 0;
  });
  // Atualiza o cursor (bolinha branca) durante play do preview
  const updateProgress = () => {
    const ws = wavesurfer.value;
    if (!ws) return;
    const dur = ws.getDuration();
    if (!dur || !isFinite(dur)) return;
    progressLeft.value = (ws.getCurrentTime() / dur) * 100;
  };
  wavesurfer.value.on('timeupdate', updateProgress);
  wavesurfer.value.on('seeking', updateProgress);
  wavesurfer.value.on('ready', updateProgress);

  record.value = wavesurfer.value.plugins[0];

  record.value.on('record-end', async blob => {
    if (wasCancelled.value) {
      wasCancelled.value = false;
      return;
    }
    try {
      const audioUrl = URL.createObjectURL(blob);
      const audioBlob = await convertAudio(blob, props.audioRecordFormat);
      const fileName = `${getUuid()}.mp3`;
      const file = new File([audioBlob], fileName, {
        type: props.audioRecordFormat,
      });
      wavesurfer.value.load(audioUrl);
      previewFile.value = file;
      progressLeft.value = 0;
      state.value = 'preview';
      emit('finishRecord', {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[AudioRecorder] convertAudio failed:', e);
      errorMessage.value = 'Falha ao processar o áudio. Tente novamente.';
      state.value = 'denied';
    }
  });

  record.value.on('record-progress', time => {
    const fmt = formatTimeProgress(time);
    elapsedText.value = fmt;
    emit('recorderProgressChanged', fmt);
  });
};

// ── Permission flow ───────────────────────────────────────────────────────
async function ensureMicrophonePermission() {
  if (!navigator.mediaDevices?.getUserMedia) {
    errorMessage.value = 'Seu navegador não suporta gravação de áudio.';
    state.value = 'denied';
    return false;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(t => t.stop());
    return true;
  } catch (e) {
    if (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError') {
      errorMessage.value = 'Acesso ao microfone foi bloqueado.';
    } else if (e?.name === 'NotFoundError') {
      errorMessage.value = 'Nenhum microfone encontrado no dispositivo.';
    } else {
      errorMessage.value = `Erro ao acessar o microfone: ${e?.message || e?.name || 'desconhecido'}.`;
    }
    state.value = 'denied';
    return false;
  }
}

// ── Actions ───────────────────────────────────────────────────────────────
async function beginFlow() {
  state.value = 'requesting';
  const ok = await ensureMicrophonePermission();
  if (!ok) return;
  // CRÍTICO: trocar pra 'recording' ANTES do initWaveSurfer pra o
  // <div ref="waveformContainer"> existir no DOM (v-if condicional).
  state.value = 'recording';
  await nextTick();
  try {
    initWaveSurfer();
    await record.value.startRecording();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[AudioRecorder] startRecording failed:', e);
    errorMessage.value = `Falha ao iniciar gravação: ${e?.message || 'desconhecido'}`;
    state.value = 'denied';
  }
}

async function retryAfterDeny() {
  errorMessage.value = '';
  await beginFlow();
}

function togglePauseResume() {
  if (!record.value) return;
  if (state.value === 'recording') {
    record.value.pauseRecording();
    state.value = 'paused';
    emit('pause');
  } else if (state.value === 'paused') {
    record.value.resumeRecording();
    state.value = 'recording';
    emit('play');
  }
}

function finalizeRecording() {
  if (!record.value) return;
  if (state.value === 'recording' || state.value === 'paused') {
    record.value.stopRecording();
    // estado vira 'preview' no handler record-end
  }
}

function cancelAll() {
  if (record.value && (state.value === 'recording' || state.value === 'paused')) {
    wasCancelled.value = true;
    try { record.value.stopRecording(); } catch (e) { /* */ }
  }
  previewFile.value = null;
  state.value = 'requesting';
  emit('cancel');
}

function togglePreviewPlayback() {
  if (!wavesurfer.value || state.value !== 'preview') return;
  wavesurfer.value.playPause();
}

// Backwards compat: parent chama playPause() ou stopRecording() via ref
function playPause() {
  if (state.value === 'preview') {
    togglePreviewPlayback();
  } else if (state.value === 'recording' || state.value === 'paused') {
    togglePauseResume();
  }
}

function stopRecording() {
  finalizeRecording();
}

// ── Atalhos teclado ───────────────────────────────────────────────────────
function onKeyDown(e) {
  if (state.value === 'denied' || state.value === 'requesting') return;
  if (e.key === 'Escape') {
    e.preventDefault();
    cancelAll();
  } else if (e.key === ' ' && state.value === 'preview') {
    e.preventDefault();
    togglePreviewPlayback();
  }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────
onMounted(async () => {
  document.addEventListener('keydown', onKeyDown);
  await beginFlow();
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeyDown);
  try {
    if (record.value && state.value === 'recording') {
      wasCancelled.value = true;
      record.value.stopRecording();
    }
  } catch (e) { /* */ }
  if (wavesurfer.value) {
    try { wavesurfer.value.destroy(); } catch (e) { /* */ }
  }
});

defineExpose({
  playPause,
  stopRecording,
  finalizeRecording,
  cancelAll,
  record,
});
</script>

<template>
  <div class="sz-ar-wrap">
    <!-- DENIED / ERROR -->
    <div v-if="state === 'denied'" class="sz-ar-pill sz-ar-error">
      <div class="sz-ar-error-content">
        <div class="sz-ar-error-title">{{ errorMessage }}</div>
        <div class="sz-ar-error-hint">{{ browserPermissionHint }}</div>
      </div>
      <div class="sz-ar-error-actions">
        <button class="sz-ar-btn-secondary" type="button" @click="retryAfterDeny">
          Tentar novamente
        </button>
        <button class="sz-ar-btn-icon" type="button" title="Cancelar" @click="cancelAll">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- REQUESTING -->
    <div v-else-if="state === 'requesting'" class="sz-ar-pill sz-ar-requesting">
      <div class="sz-ar-spinner" />
      <span class="sz-ar-label">Aguardando permissão do microfone…</span>
    </div>

    <!-- RECORDING / PAUSED / PREVIEW — único container pra preservar o
         canvas do WaveSurfer entre states (evita re-mount do <div ref> que
         desconectaria o waveform). Filhos condicionais. -->
    <div
      v-else-if="state === 'recording' || state === 'paused' || state === 'preview'"
      class="sz-ar-pill"
      :class="{
        'sz-ar-rec': state !== 'preview',
        'sz-ar-rec--paused': state === 'paused',
        'sz-ar-preview': state === 'preview',
      }"
    >
      <!-- Play button (só no preview) -->
      <button
        v-if="state === 'preview'"
        class="sz-ar-btn-circle sz-ar-btn-play"
        type="button"
        :title="isPreviewPlaying ? 'Pausar (Space)' : 'Ouvir prévia (Space)'"
        @click="togglePreviewPlayback"
      >
        <svg v-if="!isPreviewPlaying" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <polygon points="6 4 20 12 6 20"/>
        </svg>
        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>
        </svg>
      </button>

      <!-- Timer + REC dot (só durante recording/paused) -->
      <div v-if="state === 'recording' || state === 'paused'" class="sz-ar-timer-wrap">
        <span class="sz-ar-timer">{{ elapsedText || '0:00' }}</span>
        <span class="sz-ar-rec-dot" :class="{ 'sz-ar-rec-dot--paused': state === 'paused' }" />
      </div>

      <!-- Wave (presente em TODOS os states pra não re-mountar o canvas) -->
      <div class="sz-ar-wave-wrap" :class="{ 'sz-ar-wave-wrap--preview': state === 'preview' }">
        <div ref="waveformContainer" class="sz-ar-wave" />
        <div v-if="state !== 'preview'" class="sz-ar-wave-fade sz-ar-wave-fade--left" />
        <div v-if="state !== 'preview'" class="sz-ar-wave-fade sz-ar-wave-fade--right" />
        <div
          v-if="state === 'preview'"
          class="sz-ar-progress-dot"
          :style="{ left: `max(7px, min(calc(100% - 7px), ${progressLeft}%))` }"
        />
      </div>

      <!-- Timer (após wave no preview) -->
      <span v-if="state === 'preview'" class="sz-ar-timer sz-ar-timer--preview">{{ elapsedText || '0:00' }}</span>

      <!-- Trash (só no preview) -->
      <button
        v-if="state === 'preview'"
        class="sz-ar-btn-icon sz-ar-trash"
        type="button"
        title="Descartar (Esc)"
        @click="cancelAll"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* SZ_AUDIO_280_v1_20260520 — tokens do Figma do Sadraque */
.sz-ar-wrap {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 4px 0;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.sz-ar-pill {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: auto;
  max-width: 500px;
  min-width: 320px;
  flex: 0 1 auto;
  height: 56px;
  padding: 8px;
  background: #FFFFFF;
  border-radius: 8px;
  /* shadow-level-1 do Figma (Sadraque #280) */
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.1);
  position: relative;
}

/* Cauda apontando pra baixo (triangle CSS via borders).
   Posição alinhada com o botão check (3º ícone do footer). */
.sz-ar-pill.sz-ar-rec::after,
.sz-ar-pill.sz-ar-preview::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 125px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #FFFFFF;
  filter: drop-shadow(0 1px 1px rgba(15, 23, 42, 0.1));
}
:global(.dark) .sz-ar-pill.sz-ar-rec::after,
:global(.dark) .sz-ar-pill.sz-ar-preview::after,
.dark .sz-ar-pill.sz-ar-rec::after,
.dark .sz-ar-pill.sz-ar-preview::after {
  border-top-color: #2D2F32;
  filter: drop-shadow(0 1px 0 #41474E);
}

:global(.dark) .sz-ar-pill,
.dark .sz-ar-pill {
  background: #2D2F32;
}

/* Requesting */
.sz-ar-requesting {
  justify-content: center;
  color: #262626;
}
:global(.dark) .sz-ar-requesting,
.dark .sz-ar-requesting {
  color: #D4D4D4;
}
.sz-ar-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: #78BB7B;
  border-radius: 50%;
  animation: sz-ar-spin 0.7s linear infinite;
}
@keyframes sz-ar-spin {
  to { transform: rotate(360deg); }
}
.sz-ar-label {
  font-size: 14px;
  font-weight: 500;
}

/* Erro / denied */
.sz-ar-error {
  align-items: flex-start;
  padding: 12px 16px;
  height: auto;
  min-height: 56px;
}
.sz-ar-error-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sz-ar-error-title {
  font-size: 14px;
  font-weight: 600;
  color: #BA4444;
}
:global(.dark) .sz-ar-error-title,
.dark .sz-ar-error-title {
  color: #FFA2A2;
}
.sz-ar-error-hint {
  font-size: 12px;
  color: #6D6D6D;
  line-height: 1.4;
}
:global(.dark) .sz-ar-error-hint,
.dark .sz-ar-error-hint {
  color: #B0B4BA;
}
.sz-ar-error-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sz-ar-btn-secondary {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #E5E5E5;
  background: #FFFFFF;
  color: #262626;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.sz-ar-btn-secondary:hover {
  background: #F5F5F5;
}
:global(.dark) .sz-ar-btn-secondary,
.dark .sz-ar-btn-secondary {
  background: #41474E;
  border-color: #525252;
  color: #FFFFFF;
}

/* Recording / Paused */
.sz-ar-rec {
  padding: 8px;
}

.sz-ar-trash {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: 0;
  cursor: pointer;
  color: #000000;
  flex-shrink: 0;
  border-radius: 8px;
  padding: 0;
}
.sz-ar-trash:hover {
  background: rgba(0, 0, 0, 0.06);
}
:global(.dark) .sz-ar-trash,
.dark .sz-ar-trash {
  color: #FFFFFF;
}
:global(.dark) .sz-ar-trash:hover,
.dark .sz-ar-trash:hover {
  background: rgba(255, 255, 255, 0.08);
}

.sz-ar-timer-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.sz-ar-timer {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #262626;
  font-variant-numeric: tabular-nums;
}
:global(.dark) .sz-ar-timer,
.dark .sz-ar-timer {
  color: #D4D4D4;
}

.sz-ar-rec-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #BA4444;
  animation: sz-ar-pulse 1.4s ease-in-out infinite;
}
.sz-ar-rec-dot--paused {
  animation: none;
  opacity: 0.4;
}
:global(.dark) .sz-ar-rec-dot,
.dark .sz-ar-rec-dot {
  background: #FFA2A2;
}
@keyframes sz-ar-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(0.9); }
}

.sz-ar-wave-wrap {
  position: relative;
  flex: 1;
  height: 28px;
  min-width: 0;
  overflow: hidden;
}

.sz-ar-wave {
  width: 100%;
  height: 100%;
}

/* Fade nos edges do canvas — tokens Sadraque (22px wide) */
.sz-ar-wave-fade {
  position: absolute;
  top: 0;
  width: 22px;
  height: 100%;
  pointer-events: none;
}
.sz-ar-wave-fade--left {
  left: 0;
  background: linear-gradient(90deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%);
}
.sz-ar-wave-fade--right {
  right: 0;
  background: linear-gradient(270deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%);
}
:global(.dark) .sz-ar-wave-fade--left,
.dark .sz-ar-wave-fade--left {
  background: linear-gradient(90deg, #2D2F32 0%, rgba(45, 47, 50, 0) 100%);
}
:global(.dark) .sz-ar-wave-fade--right,
.dark .sz-ar-wave-fade--right {
  background: linear-gradient(270deg, #2D2F32 0%, rgba(45, 47, 50, 0) 100%);
}

/* Botão circular pause (estado recording) */
.sz-ar-btn-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
}

.sz-ar-btn-pause {
  background: #FDEAEA;
  color: #BA4444;
}
.sz-ar-btn-pause:hover {
  background: #F8D8D8;
}
:global(.dark) .sz-ar-btn-pause,
.dark .sz-ar-btn-pause {
  background: #4B3131;
  color: #FFA2A2;
}
:global(.dark) .sz-ar-btn-pause:hover,
.dark .sz-ar-btn-pause:hover {
  background: #5B3D3D;
}

/* Botão play (estado preview) */
.sz-ar-btn-play {
  background: #E8E8E8;
  color: #1E1E1E;
}
.sz-ar-btn-play:hover {
  background: #DADADA;
}
:global(.dark) .sz-ar-btn-play,
.dark .sz-ar-btn-play {
  background: #42454F;
  color: #FFFFFF;
}
:global(.dark) .sz-ar-btn-play:hover,
.dark .sz-ar-btn-play:hover {
  background: #525560;
}

/* Cursor de progresso (bolinha branca) no preview */
.sz-ar-progress-dot {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #FFFFFF;
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  z-index: 2;
}

/* Preview */
.sz-ar-preview {
  padding: 8px;
  gap: 8px;
}
.sz-ar-timer--preview {
  margin-left: 8px;
}

.sz-ar-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: 0;
  cursor: pointer;
  border-radius: 8px;
  color: #262626;
  padding: 0;
}
.sz-ar-btn-icon:hover {
  background: rgba(0, 0, 0, 0.06);
}
:global(.dark) .sz-ar-btn-icon,
.dark .sz-ar-btn-icon {
  color: #FFFFFF;
}
:global(.dark) .sz-ar-btn-icon:hover,
.dark .sz-ar-btn-icon:hover {
  background: rgba(255, 255, 255, 0.08);
}
</style>
